import { Search } from 'lucide-react';
import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Field } from './Field';
import { Kbd } from './Kbd';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  label?: ReactNode;
  hint?: ReactNode;
  /** Error message; also sets aria-invalid and the red border. `true` = red border without text. */
  error?: ReactNode | boolean;
  /** Muted tag after the label, e.g. "optional". */
  optional?: ReactNode;
  /** JetBrains Mono 13px, LTR — for URLs, IDs, paths. */
  mono?: boolean;
  /** md = 36px (default) · sm = 32px (filter bars). */
  size?: 'sm' | 'md';
  /** Content inside the box before the text (icon, "https://"…). */
  prefix?: ReactNode;
  /** Content inside the box after the text (unit, kbd, button…). */
  suffix?: ReactNode;
  /** Class for the outer field wrapper (`className` styles the root too — both are merged). */
  containerClassName?: string;
  /** Class for the bordered box. */
  boxClassName?: string;
  /** Class for the <input> element. */
  inputClassName?: string;
}

/**
 * Text input with label, hint, error, required asterisk, prefix/suffix slots and a mono variant.
 * The ref points at the <input>; `className` applies to the outer field wrapper.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hint,
    error,
    optional,
    required,
    disabled,
    mono = false,
    size = 'md',
    prefix,
    suffix,
    id,
    className,
    containerClassName,
    boxClassName,
    inputClassName,
    dir,
    ...rest
  },
  ref,
) {
  const invalid = Boolean(error);
  const errorText = typeof error === 'boolean' ? undefined : error;
  return (
    <Field
      label={label}
      hint={hint}
      error={errorText}
      required={required}
      optional={optional}
      disabled={disabled}
      htmlFor={id}
      className={cn(className, containerClassName)}
    >
      {({ controlId, describedBy }) => (
        <div
          className={cn(
            'flex w-full min-w-0 items-center gap-2 border bg-surface px-3 transition-[border-color,box-shadow] duration-150',
            size === 'sm' ? 'h-8 rounded-[6px]' : 'h-9 rounded-md',
            disabled
              ? 'border-border-2 bg-surface-2 text-text-3'
              : invalid
                ? 'border-error focus-within:shadow-focus'
                : 'border-border focus-within:border-primary focus-within:shadow-focus',
            boxClassName,
          )}
        >
          {prefix ? (
            <span className="inline-flex shrink-0 items-center text-text-3 [&>svg]:size-4">{prefix}</span>
          ) : null}
          <input
            ref={ref}
            id={controlId}
            disabled={disabled}
            required={required}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            dir={dir ?? (mono ? 'ltr' : undefined)}
            className={cn(
              'h-full w-full min-w-0 flex-1 bg-transparent text-text outline-none placeholder:text-text-3 focus-visible:shadow-none disabled:cursor-not-allowed disabled:text-text-3',
              mono ? 'font-mono text-13' : size === 'sm' ? 'text-13' : 'text-14',
              inputClassName,
            )}
            {...rest}
          />
          {suffix ? <span className="inline-flex shrink-0 items-center text-text-3">{suffix}</span> : null}
        </div>
      )}
    </Field>
  );
});

export interface SearchInputProps extends Omit<InputProps, 'prefix' | 'type'> {
  /** Shortcut hint shown at the end (default "⌘K"; pass null to hide). */
  shortcut?: string | null;
}

/** Search field with a leading magnifier and a ⌘K key-cap hint. */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { shortcut = '⌘K', suffix, inputClassName, 'aria-keyshortcuts': ariaKeyShortcuts, ...rest },
  ref,
) {
  return (
    <Input
      {...rest}
      ref={ref}
      type="search"
      prefix={<Search strokeWidth={2} />}
      suffix={
        suffix ??
        (shortcut ? (
          <Kbd aria-hidden className="pointer-events-none">
            {shortcut}
          </Kbd>
        ) : undefined)
      }
      aria-keyshortcuts={ariaKeyShortcuts ?? (shortcut ? shortcut.replace('⌘', 'Meta+') : undefined)}
      inputClassName={cn('[&::-webkit-search-cancel-button]:appearance-none', inputClassName)}
    />
  );
});
